/**
 * DCV (domain-control validation) setup dialog for an SSL cert pending issuance.
 * Fetches the per-domain DNS delegation record and lets the user copy the record
 * name + content (via NS or CNAME), then "Verify now" to re-check.
 */
import { useState } from 'react';
import { ShieldCheck, Copy } from 'lucide-react';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  IconButton,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafSslVerification,
  useTriggerWafSslVerification,
  wafExtractError,
} from '@/api/waf';

/** A label + monospaced value with a copy button (skipped when empty). */
function CopyLine({ label, text, onCopy }: { label: string; text?: string; onCopy: (t: string) => void }) {
  if (!text) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '92px 1fr auto',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      <span style={{ color: 'var(--vn-fg-muted)' }}>{label}</span>
      <code
        style={{
          fontFamily: 'var(--vn-font-mono, monospace)',
          wordBreak: 'break-all',
          background: 'var(--vn-bg-subtle)',
          padding: '2px 6px',
          borderRadius: 'var(--vn-radius-sm)',
        }}
      >
        {text}
      </code>
      <IconButton variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={() => onCopy(text)}>
        <Copy size={13} />
      </IconButton>
    </div>
  );
}

export function SslVerifyDialog({
  serviceId,
  certId,
  cn,
  open,
  onOpenChange,
}: {
  serviceId: string;
  certId: number;
  cn: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { show } = useToast();
  const [method, setMethod] = useState<'ns' | 'cname'>('cname');
  const { data, isLoading, isError, refetch } = useWafSslVerification(serviceId, certId, open);
  const trigger = useTriggerWafSslVerification(serviceId, certId, {
    onSuccess: () => show({ tone: 'success', title: 'Verification re-checked' }),
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Verification failed') }),
  });

  const copy = (t: string) => {
    void navigator.clipboard.writeText(t);
    show({ tone: 'success', title: 'Copied' });
  };

  const domains = data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Set up verification — {cn}</DialogTitle>
          <DialogDescription>
            Publish the records below at your DNS / origin to prove domain control, then verify.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {isLoading ? (
            Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} style={{ height: 96 }} />)
          ) : isError ? (
            <Alert tone="danger" title="Couldn't load verification" actions={<button onClick={() => refetch()}>Retry</button>}>
              The verification challenges couldn't be loaded.
            </Alert>
          ) : domains.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={20} />}
              title="No pending verification"
              description="This certificate has no domain-control challenges pending."
            />
          ) : (
            <>
              {/* DNS verification method — NS or CNAME delegation record. */}
              <div style={{ display: 'inline-flex', gap: 'var(--vn-space-2)' }}>
                <Button size="sm" variant={method === 'ns' ? 'primary' : 'secondary'} onClick={() => setMethod('ns')}>
                  NS Record
                </Button>
                <Button size="sm" variant={method === 'cname' ? 'primary' : 'secondary'} onClick={() => setMethod('cname')}>
                  CNAME Record
                </Button>
              </div>

              {domains.map((d) => {
                const rec = method === 'ns' ? d.dns.ns : d.dns.cname;
                return (
                  <div key={d.domain} style={{ display: 'grid', gap: 4 }}>
                    <div style={{ fontWeight: 500 }}>{d.domain}</div>
                    <CopyLine label="Record name" text={rec.name} onCopy={copy} />
                    <CopyLine label="Content" text={rec.value} onCopy={copy} />
                  </div>
                );
              })}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            startIcon={<ShieldCheck size={15} />}
            disabled={trigger.isPending || isLoading || isError}
            onClick={() => trigger.mutate()}
          >
            Verify now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SslVerifyDialog;
